import React from 'react';

/**
 * Palette, type and component styles for the public marketing pages.
 *
 * Deliberately separate from the app's theme tokens: the site is dark, the app
 * is whatever the user chose, and neither should be able to disturb the other.
 * The app's own tokens are remapped inside .lp-page so shared components — the
 * Logo especially — render against this palette instead of inheriting a theme
 * that would make them invisible here.
 *
 * Shared by LandingPage and PricingPage so the two cannot drift.
 */
export function LandingChrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="lp-page min-h-screen">
        <style>{`
          /* ── Landing page owns its palette. Deliberately independent of the
             app's theme tokens: the site is dark, the app stays as it is. ── */
          .lp-page {
            --lp-ground:      #0a0b0d;
            --lp-raise:       #101216;
            --lp-raise-2:     #16181e;
            --lp-grout:       rgba(255,255,255,.075);
            --lp-grout-hard:  rgba(255,255,255,.13);
            --lp-ink:         #f3f5f8;
            --lp-mid:         rgba(243,245,248,.64);
            --lp-low:         rgba(243,245,248,.38);
            --lp-hover:       rgba(255,255,255,.06);
            --lp-shadow:      0 24px 60px -28px rgba(0,0,0,.9);

            /* Tesserae — the designed jewel set, not the app's category colours */
            --t-gold:   #E0A22B;
            --t-coral:  #E4664F;
            --t-teal:   #2FA39B;
            --t-violet: #7B62C9;
            --t-lime:   #8FB33E;
            --t-sky:    #4A8FD4;

            --lp-accent:      var(--t-gold);
            --lp-accent-2:    #EFB44A;
            --lp-accent-soft: rgba(224,162,43,.14);
            --lp-accent-ink:  #0a0b0d;

            /* The Logo and other shared components read the APP's tokens. Without
               remapping, a visitor whose app theme is light gets a near-black
               wordmark on a near-black page — invisible. Point them at the
               landing palette so anything dropped in here renders correctly. */
            --content-primary:   var(--lp-ink);
            --content-secondary: var(--lp-mid);
            --content-tertiary:  var(--lp-low);
            --accent-primary:    var(--lp-accent);
            --accent-hover:      var(--lp-accent-2);
            --accent-subtle:     var(--lp-accent-soft);
            --border-primary:    var(--lp-grout);
            --mosaic-dark:       var(--lp-ink);

            background: var(--lp-ground);
            color: var(--lp-ink);
            color-scheme: dark;
          }

          /* Display face carries the personality; body stays neutral */
          .lp-page main h2, .lp-page .mh-h1 {
            font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
            font-weight: 400;
            letter-spacing: -.012em;
          }
          .lp-page main h2 { font-size: clamp(28px, 3.6vw, 46px); line-height: 1.06; }

          /* ── Buttons ─────────────────────────────────────────────────── */
          .btn-primary, .btn-ghost, .btn-outline {
            display: inline-flex; align-items: center; justify-content: center;
            border-radius: 8px; font-size: 13.5px; font-weight: 500;
            padding: 9px 15px; white-space: nowrap; cursor: pointer;
            border: 1px solid transparent;
            transition: background-color .15s, color .15s, border-color .15s;
          }
          /* 48px so the primary CTA is thumb-friendly on mobile */
          .btn-lg { font-size: 15px; padding: 13px 22px; border-radius: 9px; min-height: 48px; }
          .btn-primary { background: var(--lp-accent); color: var(--lp-accent-ink); }
          .btn-primary:hover { background: var(--lp-accent-2); }
          .btn-ghost { color: var(--lp-mid); background: transparent; }
          .btn-ghost:hover { background: var(--lp-hover); color: var(--lp-ink); }
          .btn-outline { background: transparent; color: var(--lp-ink); border-color: var(--lp-grout-hard); }
          .btn-outline:hover { background: var(--lp-hover); }
          .btn-primary:focus-visible, .btn-ghost:focus-visible, .btn-outline:focus-visible {
            outline: 2px solid var(--lp-accent); outline-offset: 2px;
          }
          .eyebrow {
            font-size: 11.5px; font-weight: 500; letter-spacing: .09em;
            text-transform: uppercase; margin: 0;
          }

          /* ── Mosaic hero ─────────────────────────────────────────────── */
          .mh-zone { position: relative; height: 300vh; }
          .mh-stage { position: sticky; top: 4rem; height: calc(100svh - 4rem); min-height: 520px; }
          .mh-frame {
            position: relative; height: 100%; overflow: hidden;
            background: radial-gradient(120% 92% at 50% 4%, #12141a 0%, var(--lp-ground) 64%);
            border-bottom: 1px solid var(--lp-grout);
          }
          .mh-canvas { position: absolute; inset: 0; }
          .mh-tile {
            position: absolute; border-radius: 3px; overflow: hidden;
            white-space: nowrap; font-size: 9.5px; letter-spacing: .01em;
            display: flex; align-items: center; padding: 0 4px;
            will-change: transform, opacity;
          }
          .mh-stripe { position: absolute; background: rgba(255,255,255,.03); }
          .mh-tag {
            position: absolute; font-size: 8.5px; letter-spacing: .09em;
            font-weight: 500; color: var(--lp-low);
            /* Bounded so a label can never reach under the first bar */
            width: 24px; overflow: hidden; white-space: nowrap;
          }
          .mh-overlay {
            position: absolute; left: 0; right: 0; bottom: 0; z-index: 5;
            padding: 0 clamp(20px, 5vw, 64px) clamp(34px, 6vh, 68px);
            background: linear-gradient(to top, var(--lp-ground) 34%, rgba(10,11,13,.72) 62%, transparent);
            pointer-events: none;
          }
          .mh-copy {
            transition: opacity .17s ease, transform .17s ease;
          }
          .mh-copy.swapping { opacity: 0; transform: translateY(8px); }
          @media (prefers-reduced-motion: reduce) {
            .mh-copy { transition: none; }
            .mh-copy.swapping { opacity: 1; transform: none; }
          }
          .mh-h1 {
            margin: 0; color: var(--lp-ink);
            font-size: clamp(34px, 6.4vw, 82px); line-height: 1.0;
          }
          .mh-h1 em { font-style: italic; opacity: .68; }
          .mh-sub { margin: 14px 0 0; max-width: 48ch; color: var(--lp-mid); font-size: 15px; line-height: 1.55; }
          .mh-cta { margin-top: 22px; display: flex; align-items: center; gap: 16px; flex-wrap: wrap; pointer-events: auto; }
          .mh-risk { font-size: 13px; color: var(--lp-low); }

          @media (prefers-reduced-motion: reduce) {
            .mh-zone { height: auto; }
            .mh-stage { position: static; height: 78svh; top: auto; }
          }
        `}</style>
      {children}
    </div>
  );
}
